import { SignalBase } from './signal_base';
import { ResDeviceType, SpeakerWebrtcAction, PresetWebrtcAction, EventWebrtcAction, CameraSpeakerAction } from "./base";
import { DownloaderWebrtcAction } from './downloader';
const maxRetryTimes = Number.MAX_SAFE_INTEGER;
// one time websocket
class WS {
    constructor(options) {
        this.ready = false;
        this.closed = false;
        this.retryTimes = 0;
        this.debugging = options.debugging ?? false;
        this.url = options.wsUrl;
        this.sessionId = options.sessionId;
        this.authToken = options.authToken;
        this.onOpen = options.wsOnOpen;
        this.onReady = options.wsOnReady;
        this.onError = options.wsOnError;
        this.onClose = options.wsOnClose;
        this.onMessage = options.wsOnMessage;
        this.connect();
        this.setTimer();
    }
    clearInterval() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }
    checkOpen() {
        try {
            if (this.ws.readyState !== WebSocket.OPEN) {
                return new Error('ws not open');
            }
        }
        catch (e) {
            console.error(e);
            return new Error('ws not open');
        }
        return null;
    }
    close() {
        if (this.closed)
            return;
        this.closed = true;
        this.doClose();
    }
    doClose() {
        this.clearInterval();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        if (this.onClose) {
            this.onClose();
        }
    }
    setTimer() {
        this.clearInterval();
        this.timer = setInterval(() => {
            if (this.closed)
                return;
            if (this.ws) {
                if (performance.now() - this.ws['createdAt'] >= WS.TIMER_INTERVAL)
                    this.setPing();
            }
            else {
                this.doReconnect();
            }
        }, WS.TIMER_INTERVAL);
    }
    setPing() {
        const ws = this.ws;
        console.info('ws ping');
        this.sendMessage('ping');
        ws['received'] = false;
        setTimeout(() => {
            if (this.ws === ws) {
                if (!ws['received']) {
                    console.log('ws ping timeout');
                    this.reconnect();
                }
            }
            else {
                console.warn('ws ping->ignored');
            }
            if (!ws['received'])
                ws.close();
        }, WS.PING_TIMEOUT);
    }
    connect() {
        const ws = new WebSocket(`${this.url}?session_id=${this.sessionId}`, [this.authToken]);
        ws.onopen = () => {
            if (ws === this.ws) {
                console.info('ws open');
            }
            else {
                console.warn('ws open->ignored');
                return;
            }
            this.retryTimes = 0;
            if (this.onOpen) {
                this.onOpen();
            }
            if (!this.ready) {
                this.ready = true;
                if (this.onReady)
                    this.onReady();
            }
        };
        ws.onerror = (e) => {
            if (ws === this.ws) {
                console.info('ws error');
            }
            else {
                console.warn('ws error->ignored');
                return;
            }
            if (this.onError) {
                this.onError(new Error('ws error'));
            }
        };
        ws.onclose = () => {
            if (ws === this.ws) {
                console.info('ws close');
                this.reconnect();
            }
            else {
                console.warn('ws close->ignored');
                return;
            }
        };
        ws.onmessage = (ev) => {
            if (ev.data === 'pong') {
                console.log('ws pong');
                ws['received'] = true;
                return;
            }
            if (ws === this.ws) {
                if (this.debugging)
                    console.debug('ws message:', ev.data);
            }
            else {
                if (this.debugging)
                    console.warn('ws message->ignored:', ev.data);
                return;
            }
            if (this.onMessage) {
                this.onMessage(ev.data);
            }
        };
        ws['createdAt'] = performance.now();
        this.ws = ws;
    }
    doReconnect() {
        if (this.retryTimes++ < maxRetryTimes) {
            console.log("ws doReconnect");
            this.connect();
        }
    }
    reconnect() {
        if (!this.closed) {
            this.ws = null;
            if (this.retryTimes === 0) {
                console.log('ws fast reconnect');
                this.doReconnect();
            }
        }
    }
    sendMessage(message) {
        const error = this.checkOpen();
        if (error)
            return error;
        this.ws.send(message);
    }
}
WS.TIMER_INTERVAL = 10000;
WS.PING_TIMEOUT = 5000;
export class Signal extends SignalBase {
    close() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
    constructor(options) {
        super({ debugging: options.debugging ?? false });
        this.options = options;
        this.connect();
    }
    static async processResponse(response) {
        const json = await response.json();
        let err = null, ret = null;
        if (response.status >= 200 && response.status < 300) {
            ret = json;
        }
        else {
            err = json;
        }
        return { ret, err };
    }
    static async getMe(authToken) {
        return await fetch(this.baseUrl + "/auth/me", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
            }
        }).then(this.processResponse);
    }
    static async getSites(authToken, orgId) {
        return await fetch(this.baseUrl + "/site/tree", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            }
        }).then(this.processResponse);
    }
    static async getTurns(authToken, orgId, nvrId, sessionId) {
        return await fetch(this.baseUrl + "/webrtc/user/obtain_credentials", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            },
            body: JSON.stringify({
                "deviceId": nvrId,
                "sessionId": sessionId,
                "type": "nvr",
            })
        }).then(this.processResponse);
    }
    static async getCameras(authToken, orgId, siteId, paging) {
        if (!paging)
            paging = { offset: 0, limit: 100 };
        let path = `/camera/cameras?page.offset=${paging.offset}&page.limit=${paging.limit}`;
        if (siteId)
            path += '&siteId=' + siteId;
        path += '&withChannelId=true';
        return await fetch(this.baseUrl + path, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            }
        }).then(this.processResponse);
    }
    static async getIpSpeakers(authToken, orgId, query = "") {
        return await fetch(this.baseUrl + '/speaker/speakers' + query, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            }
        }).then(this.processResponse);
    }
    static async getNvr(authToken, orgId, nvrId) {
        return await fetch(this.baseUrl + `/device/nvrs/${nvrId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            }
        }).then(this.processResponse);
    }
    static async getCustomers(authToken, orgId) {
        return await fetch(this.baseUrl + `/user/customers`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            }
        }).then(this.processResponse);
    }
    static async getPtzToken(authToken, orgId, cameraId) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, ["ptz.ctrl"], [cameraId]);
    }
    static async getResActToken(authToken, orgId, resType, actions, resIdList) {
        const resourceIds = resIdList.map((resId) => typeof resId === "string" ? ({ sid: resId }) : ({ id: resId }));
        return await fetch(this.baseUrl + `/perm/perms/third`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken,
                "x-org-id": orgId.toString()
            },
            body: JSON.stringify({
                data: [{
                        actions: actions,
                        resourceIds: resourceIds,
                        resourceType: resType
                    }],
            })
        }).then(this.processResponse);
    }
    static async getSpeakerConfigToken(authToken, orgId, speakerId) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.SPEAKER, [SpeakerWebrtcAction.CONFIG], [speakerId]);
    }
    static async getSpeakerSpeakToken(authToken, orgId, speakerId) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.SPEAKER, [SpeakerWebrtcAction.SPEAK], [speakerId]);
    }
    static async getCameraSpeakToken(authToken, orgId, cameraId) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, [CameraSpeakerAction.SPEAK], [cameraId]);
    }
    static async getWebrtcDownloadToken(authToken, orgId, cameraIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, [DownloaderWebrtcAction.Download], cameraIds);
    }
    static async getWebrtcListToken(authToken, orgId, nvrIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.DOWNLOADER, [DownloaderWebrtcAction.List], nvrIds);
    }
    static async getWebrtcGetMediaToken(authToken, orgId, eventIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.EVENT, [EventWebrtcAction.GetMedia], eventIds);
    }
    static async getWebrtcDownloadCompleteToken(authToken, orgId, cameraIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, [DownloaderWebrtcAction.Complete], cameraIds);
    }
    static async getWebrtcDownloadAbortToken(authToken, orgId, cameraIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, [DownloaderWebrtcAction.Abort], cameraIds);
    }
    static async getSpeakerPlayToken(authToken, orgId, speakerId) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.SPEAKER, [SpeakerWebrtcAction.PLAY], [speakerId]);
    }
    static async getWebrtcPresetToken(authToken, orgId, cameraIds) {
        return await Signal.getResActToken(authToken, orgId, ResDeviceType.CAMERA, [PresetWebrtcAction.LIST, PresetWebrtcAction.DELETE, PresetWebrtcAction.GET_IMG, PresetWebrtcAction.GOTO, PresetWebrtcAction.SET], cameraIds);
    }
    sendMessage(obj) {
        return this.ws.sendMessage(JSON.stringify(obj));
    }
    sendWithTimeout(options) {
        return super.sendTimeout({
            obj: { request: options.request, headers: options.headers },
            timeout: options.timeout,
            callback: options.callback
        });
    }
    connect() {
        if (this.ws) {
            console.log('call signal.connect');
            this.ws.close();
            this.ws = null;
        }
        const the = this.options;
        const options = {
            sessionId: the.sessionId, authToken: the.authToken, wsOnOpen: the.wsOnOpen, wsUrl: Signal.wsUrl,
            wsOnClose: the.wsOnClose, wsOnError: the.wsOnError, debugging: this.debugging, wsOnReady: the.wsOnReady,
            wsOnMessage: (message) => {
                const data = JSON.parse(message);
                this.onMessage(data.result);
                the?.wsOnMessageExternal?.(data);
            }
        };
        this.ws = new WS(options);
    }
}
Signal.baseUrl = "https://dev-api.agi7.ai/agi7/api";
Signal.wsUrl = 'wss://dev-ws.agi7.ai/agi7/api/user/ws';
